import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdBuild, MdLock, MdEdit, MdDelete } from 'react-icons/md';
import { apiService, graphBuilderApi } from '../../services/api';
import { hasRoles, getCurrentUser } from '../../utils/roleCheck';
import ImportGraphModal from './ImportGraphModal';
import './GraphManagement.css';

function GraphsPage() {
  const navigate = useNavigate();
  const [graphs, setGraphs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = React.useRef(null);
  const [filteredGraphs, setFilteredGraphs] = useState([]);

  // Graph tag state (foundational/case/ontology) -- read from manifest.
  const [graphTagsById, setGraphTagsById] = useState({});
  const [tagFilter, setTagFilter] = useState('all'); // 'all' | 'foundational' | 'case' | 'untagged'

  // Per-graph SER + Redis-cache status, fetched in bulk after the page
  // of graphs loads. Shape:
  //   { [graphId]: { has_ser_receptors, ser_compiled_at,
  //                  in_redis_cache,    redis_cached_at,
  //                  compiled_full,     compiled_at } }
  const [graphMetaById, setGraphMetaById] = useState({});

  // Workspace filter (client-side, name-based) -- persisted across reloads
  // so the user's last choice sticks. 'all' shows every graph.
  const [workspaceFilter, setWorkspaceFilter] = useState(
    () => localStorage.getItem('graphListWorkspaceFilter') || 'all'
  );
  
  // Column visibility toggle (persisted in localStorage)
  const [showExtraColumns, setShowExtraColumns] = useState(
    () => localStorage.getItem('graphListShowExtra') === 'true'
  );
  const toggleExtraColumns = () => {
    setShowExtraColumns(prev => {
      const next = !prev;
      localStorage.setItem('graphListShowExtra', String(next));
      return next;
    });
  };
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedGraph, setSelectedGraph] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    graphName: '',
    workspaceId: '',
    description: '',
    graphType: 'Hybrid',
    status: 'Draft',
    isLocked: false
  });
  
  // Workspaces for dropdown
  const [workspaces, setWorkspaces] = useState([]);
  
  // Delete confirmation (two-step process)
  const [deleteStep, setDeleteStep] = useState(1); // 1 = first warning, 2 = final confirmation
  const [deleteNumbers, setDeleteNumbers] = useState(['', '', '']);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [metadataText, setMetadataText] = useState('');
  const [metadataError, setMetadataError] = useState(null);
  
  // Permissions state
  const [permissions, setPermissions] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPermissionType, setSelectedPermissionType] = useState('Read');
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // Check if current user can create graphs
  const user = getCurrentUser();
  const canCreateGraphs = hasRoles(user?.roles, ['CerebralAdmin', 'CompanyAdmin', 'WorkspaceAdmin', 'GraphAdmin']);
  const canBuildGraphs = hasRoles(user?.roles, ['WorkspaceAdmin', 'GraphAdmin', 'CompanyAdmin', 'GraphDataScientist']);
  const isAdmin = hasRoles(user?.roles, ['CerebralAdmin', 'CompanyAdmin']);

  // Foundational build job state.
  const [foundationalJob, setFoundationalJob] = useState(null);
  const [foundationalError, setFoundationalError] = useState(null);
  const foundationalPollRef = React.useRef(null);

  const startFoundationalBuild = async ({ rebuildAll = false } = {}) => {
    if (foundationalJob && (foundationalJob.status === 'queued' || foundationalJob.status === 'running')) {
      return;
    }
    setFoundationalError(null);
    try {
      const r = await graphBuilderApi.buildFoundationalCorpus({ rebuildAll });
      setFoundationalJob({ ...r, log_tail: [] });
      if (foundationalPollRef.current) clearInterval(foundationalPollRef.current);
      foundationalPollRef.current = setInterval(async () => {
        try {
          const status = await graphBuilderApi.getFoundationalBuildStatus(r.job_id);
          setFoundationalJob(status);
          if (status.status === 'completed' || status.status === 'failed') {
            clearInterval(foundationalPollRef.current);
            foundationalPollRef.current = null;
          }
        } catch (e) {
          // ignore transient errors
        }
      }, 4000);
    } catch (err) {
      setFoundationalError(err?.response?.data?.detail || err?.message || 'Build failed');
    }
  };

  useEffect(() => () => {
    if (foundationalPollRef.current) clearInterval(foundationalPollRef.current);
  }, []);

  // Debounce search term -> debouncedSearch (300ms)
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchTerm]);

  // Reset to page 1 when workspace or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [workspaceFilter, debouncedSearch]);

  // Load data whenever page, workspace, or debounced search changes
  useEffect(() => {
    loadGraphs();
  }, [currentPage, pageSize, workspaceFilter, debouncedSearch]);

  // Load manifest-derived graph tags once. The list is cheap (a single
  // file read on the GraphBuilder side) and updates rarely.
  useEffect(() => {
    let cancelled = false;
    graphBuilderApi.listGraphTags()
      .then((data) => {
        if (cancelled) return;
        setGraphTagsById(data?.by_graph_id || {});
      })
      .catch(() => { /* tag info is optional; ignore */ });
    return () => { cancelled = true; };
  }, []);

  // Populate the workspace dropdown on mount so the filter is always
  // ready (create/edit modal flows also call this lazily, but the
  // filter needs it eagerly).
  useEffect(() => {
    loadWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Loading flag for the bulk SER + Redis lookup. Used so the SER /
  // Cache columns can render an unambiguous "..." placeholder instead
  // of falsely showing "No" while the request is still in flight.
  const [graphMetaLoading, setGraphMetaLoading] = useState(false);

  // After each page of graphs lands, ask GraphBuilder for the SER + Redis
  // cache flags in one bulk call. Failures are non-fatal -- the new
  // columns just render '-' until/if the call succeeds.
  useEffect(() => {
    const ids = (graphs || []).map((g) => g.graphId).filter(Boolean);
    if (ids.length === 0) {
      setGraphMetaById({});
      setGraphMetaLoading(false);
      return undefined;
    }
    let cancelled = false;
    // Reset prior page's meta so a row never shows stale Yes/No while a
    // new bulk request is in flight.
    setGraphMetaById({});
    setGraphMetaLoading(true);
    graphBuilderApi.getBulkCacheStatus(ids)
      .then((data) => {
        if (cancelled) return;
        setGraphMetaById(data?.statuses || {});
      })
      .catch(() => {
        if (cancelled) return;
        setGraphMetaById({});
      })
      .finally(() => {
        if (cancelled) return;
        setGraphMetaLoading(false);
      });
    return () => { cancelled = true; };
  }, [graphs]);

  // Filter graphs by tag (client-side only — tags come from GraphBuilder, not DB).
  // Workspace and search filtering are now server-side via the API.
  useEffect(() => {
    let result = graphs;
    if (tagFilter !== 'all') {
      result = result.filter((graph) => {
        const tag = graphTagsById[graph.graphId] || null;
        if (tagFilter === 'untagged') return !tag;
        return tag === tagFilter;
      });
    }
    setFilteredGraphs(result);
  }, [graphs, tagFilter, graphTagsById]);

  const loadGraphs = async () => {
    try {
      setLoading(true);
      setError(null);
      // Resolve workspace name to ID for server-side filtering
      const wsId = workspaceFilter !== 'all'
        ? (workspaces.find(w => w.workspaceName === workspaceFilter) || {}).workspaceId || undefined
        : undefined;
      const response = await apiService.listGraphs(currentPage, pageSize, {
        workspaceId: wsId,
        search: debouncedSearch || undefined,
      });
      
      if (response.success) {
        setGraphs(response.graphs || []);
        setTotalCount(response.totalCount || 0);
        setTotalPages(response.totalPages || 0);
      } else {
        setError('Failed to load graphs');
      }
    } catch (err) {
      console.error('Error loading graphs:', err);
      setError(err.response?.data?.message || 'Failed to load graphs');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkspaces = async () => {
    try {
      const response = await apiService.getWorkspacesForDropdown();
      if (response.success) {
        const list = response.workspaces || [];
        setWorkspaces(list);

        // If the persisted workspace filter no longer exists (workspace
        // was deleted or the user lost access), silently reset to 'all'
        // so the page doesn't get stuck showing an empty result set.
        const saved = localStorage.getItem('graphListWorkspaceFilter');
        if (saved && saved !== 'all' && !list.some((w) => w.workspaceName === saved)) {
          setWorkspaceFilter('all');
          localStorage.removeItem('graphListWorkspaceFilter');
        }
      }
    } catch (err) {
      console.error('Error loading workspaces:', err);
    }
  };

  const handleCreateGraph = () => {
    setFormData({
      graphName: '',
      workspaceId: '',
      description: '',
      graphType: 'Hybrid',
      status: 'Draft',
      isLocked: false
    });
    loadWorkspaces();
    setShowCreateModal(true);
  };

  const handleEditGraph = async (graph) => {
    try {
      const response = await apiService.getGraphDetails(graph.graphId);
      if (response.success) {
        setSelectedGraph(response.graph);
        setFormData({
          graphName: response.graph.graphName,
          workspaceId: response.graph.workspaceId,
          description: response.graph.description || '',
          graphType: response.graph.graphType || 'Hybrid',
          status: response.graph.status || 'Draft',
          isLocked: response.graph.isLocked || false
        });
        const meta = response.graph.metadata;
        setMetadataText(
          meta && typeof meta === 'object' && Object.keys(meta).length
            ? JSON.stringify(meta, null, 2)
            : ''
        );
        setMetadataError(null);
        loadWorkspaces();
        setShowEditModal(true);
      }
    } catch (err) {
      console.error('Error loading graph details:', err);
      setError(err.response?.data?.message || 'Failed to load graph details');
    }
  };

  const handleDeleteGraph = (graph) => {
    setSelectedGraph(graph);
    setDeleteStep(1); // Start at step 1
    setDeleteNumbers(['', '', '']);
    setDeleteError(null);
    setIsDeleting(false);
    setShowDeleteModal(true);
  };

  const handleFirstConfirmation = () => {
    // Move to step 2 (final confirmation with code entry)
    setDeleteStep(2);
    setDeleteError(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteStep(1);
    setDeleteNumbers(['', '', '']);
    setDeleteError(null);
    setIsDeleting(false);
  };

  const handleManagePermissions = (graph) => {
    setSelectedGraph(graph);
    loadPermissions(graph.graphId);
    setShowPermissionsModal(true);
  };

  const handleBuildGraph = (graph) => {
    navigate(`/user-home/graphs/build/${graph.graphId}`);
  };

  const submitCreateGraph = async (e) => {
    e.preventDefault();
    if (isCreating) return; // Prevent double submission
    setIsCreating(true);
    try {
      const response = await apiService.createGraph(formData);
      if (response.success) {
        setSuccess('Graph created successfully');
        setShowCreateModal(false);
        loadGraphs();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || 'Failed to create graph');
      }
    } catch (err) {
      console.error('Error creating graph:', err);
      setError(err.response?.data?.message || 'Failed to create graph');
    } finally {
      setIsCreating(false);
    }
  };

  const submitEditGraph = async (e) => {
    e.preventDefault();
    let metadata = {};
    const trimmed = metadataText.trim();
    if (trimmed) {
      try {
        metadata = JSON.parse(trimmed);
      } catch (err) {
        setMetadataError('Metadata is not valid JSON');
        return;
      }
      if (metadata === null || typeof metadata !== 'object' || Array.isArray(metadata)) {
        setMetadataError('Metadata must be a JSON object');
        return;
      }
    }
    setMetadataError(null);
    try {
      const response = await apiService.updateGraph(selectedGraph.graphId, {
        ...formData,
        metadata,
      });
      if (response.success) {
        setSuccess('Graph updated successfully');
        setShowEditModal(false);
        loadGraphs();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || 'Failed to update graph');
      }
    } catch (err) {
      console.error('Error updating graph:', err);
      setError(err.response?.data?.message || 'Failed to update graph');
    }
  };

  const submitDeleteGraph = async () => {
    const confirmationCode = deleteNumbers.join('');
    if (confirmationCode.length !== 3) {
      setDeleteError('Please enter 3 digits');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await apiService.deleteGraph(selectedGraph.graphId, confirmationCode);
      if (response.success) {
        setSuccess('Graph and all related data (nodes, connections, documents, Q&A, vectors) deleted permanently');
        setShowDeleteModal(false);
        setDeleteStep(1);
        setDeleteNumbers(['', '', '']);
        loadGraphs();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setDeleteError(response.message || 'Failed to delete graph');
      }
    } catch (err) {
      console.error('Error deleting graph:', err);
      setDeleteError(err.response?.data?.message || 'Failed to delete graph');
    } finally {
      setIsDeleting(false);
    }
  };

  const loadPermissions = async (graphId) => {
    try {
      setPermissionsLoading(true);
      const response = await apiService.getGraphPermissions(graphId);
      if (response.success) {
        setPermissions(response.permissions || []);
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const searchUsers = async (term) => {
    if (!term || term.trim() === '') {
      setSearchedUsers([]);
      return;
    }
    try {
      const response = await apiService.searchGraphUsers(term);
      if (response.success) {
        setSearchedUsers(response.users || []);
      }
    } catch (err) {
      console.error('Error searching users:', err);
    }
  };

  const grantPermission = async () => {
    if (!selectedUser) {
      return;
    }
    try {
      const response = await apiService.grantGraphPermission(
        selectedGraph.graphId,
        selectedUser.userId,
        selectedPermissionType
      );
      if (response.success) {
        setSuccess('Permission granted successfully');
        loadPermissions(selectedGraph.graphId);
        setSelectedUser(null);
        setUserSearchTerm('');
        setSearchedUsers([]);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Error granting permission:', err);
      setError(err.response?.data?.message || 'Failed to grant permission');
    }
  };

  const revokePermission = async (accessControlId) => {
    if (!window.confirm('Are you sure you want to revoke this permission?')) {
      return;
    }
    try {
      const response = await apiService.revokeGraphPermission(selectedGraph.graphId, accessControlId);
      if (response.success) {
        setSuccess('Permission revoked successfully');
        loadPermissions(selectedGraph.graphId);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Error revoking permission:', err);
      setError(err.response?.data?.message || 'Failed to revoke permission');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Compact date+time formatter for the small-font "last compiled" /
  // "last cached" sub-line under the Yes/No pill.
  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return `${date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })} ${date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })}`;
  };

  const getTruncatedEmail = (email) => {
    if (!email) return 'N/A';
    const atIndex = email.indexOf('@');
    return atIndex > 0 ? email.substring(0, atIndex) : email;
  };

  return (
    <div className="graph-management-page">
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Graph Management</h1>
          <p className="page-subtitle">Manage your AI model graphs</p>
        </div>
        <div className="header-right">
          {canCreateGraphs && (
            <>
              <button className="btn-create" onClick={() => setShowImportModal(true)} style={{ marginRight: '8px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}>
                Import Graph
              </button>
              <button className="btn-create" onClick={() => navigate('/user-home/graphs/bulk-build')} style={{ marginRight: '8px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}>
                Bulk Build
              </button>
              {isAdmin && (
                <button
                  className="btn-create"
                  onClick={() => startFoundationalBuild({ rebuildAll: false })}
                  disabled={foundationalJob && (foundationalJob.status === 'queued' || foundationalJob.status === 'running')}
                  style={{ marginRight: '8px', background: '#ecfdf5', color: '#047857', border: '1px solid #6ee7b7' }}
                  title="Run build_reference_corpus.py for any manifest entries with graph_id == null"
                >
                  ⚙ Build Foundational Corpus
                </button>
              )}
              <button className="btn-create" onClick={handleCreateGraph}>
                + Create New Graph
              </button>
            </>
          )}
        </div>
      </div>

      {/* Foundational build status banner */}
      {foundationalJob && (
        <div className={`alert alert-${foundationalJob.status === 'failed' ? 'error' : 'info'}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <strong>Foundational corpus build:</strong> {foundationalJob.status}
            {foundationalJob.return_code != null && ` (rc=${foundationalJob.return_code})`}
            {foundationalJob.error && ` — ${foundationalJob.error}`}
          </span>
          {foundationalJob.log_tail && foundationalJob.log_tail.length > 0 && (
            <details style={{ marginLeft: 12 }}>
              <summary style={{ cursor: 'pointer' }}>log tail ({foundationalJob.log_tail.length})</summary>
              <pre style={{ maxHeight: 240, overflow: 'auto', fontSize: 11, marginTop: 6 }}>
                {foundationalJob.log_tail.slice(-30).join('\n')}
              </pre>
            </details>
          )}
        </div>
      )}
      {foundationalError && <div className="alert alert-error">{foundationalError}</div>}

      {/* Success/Error Messages */}
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search graphs by name, workspace, type, status, or creator..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="graph-tag-filter"
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          title="Filter by graph tag (foundational, case, ontology, untagged)"
        >
          <option value="all">All tags</option>
          <option value="foundational">Foundational</option>
          <option value="case">Case</option>
          <option value="ontology">Ontology</option>
          <option value="untagged">Untagged</option>
        </select>
        <select
          className="graph-tag-filter"
          value={workspaceFilter}
          onChange={(e) => {
            const v = e.target.value;
            setWorkspaceFilter(v);
            if (v === 'all') {
              localStorage.removeItem('graphListWorkspaceFilter');
            } else {
              localStorage.setItem('graphListWorkspaceFilter', v);
            }
          }}
          title="Filter by workspace"
        >
          <option value="all">All workspaces</option>
          {workspaces.map((w) => (
            <option key={w.workspaceId} value={w.workspaceName}>
              {w.workspaceName}
            </option>
          ))}
        </select>
        <button
          className="btn-toggle-columns"
          onClick={toggleExtraColumns}
          title={showExtraColumns ? 'Hide extra columns' : 'Show extra columns'}
        >
          {showExtraColumns ? 'Hide Details' : 'Show Details'}
        </button>
      </div>

      {/* Graphs Grid */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading graphs...</p>
        </div>
      ) : filteredGraphs.length === 0 ? (
        <div className="empty-state">
          <p>No graphs found</p>
          {canCreateGraphs && (
            <button className="btn-create" onClick={handleCreateGraph}>
              Create Your First Graph
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Graph Name</th>
                  <th>Tag</th>
                  <th className="num-cell">#Nodes</th>
                  <th className="status-cell">SER Compiled</th>
                  <th className="status-cell">In Cache</th>
                  <th>Workspace</th>
                  <th>Description</th>
                  {showExtraColumns && <th>Type</th>}
                  {showExtraColumns && <th>Status</th>}
                  {showExtraColumns && <th>Locked</th>}
                  {showExtraColumns && <th>Created By</th>}
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGraphs.map((graph) => {
                  const tag = graphTagsById[graph.graphId] || null;
                  const meta = graphMetaById[graph.graphId];
                  const metaPending = graphMetaLoading && !meta;
                  // SER status is the authoritative GraphStats."SERCompiledAt"
                  // returned directly by /api/graphs. The bulk endpoint is no
                  // longer the source of truth here.
                  const serCompiled = !!graph.serCompiled;
                  const serCompiledAt = formatDateTime(graph.serCompiledAt);
                  const redisCachedAt = formatDateTime(meta?.redis_cached_at);
                  return (
                  <tr key={graph.graphId}>
                    <td className="graph-name">{graph.graphName}</td>
                    <td>
                      {tag ? (
                        <span className={`graph-tag-pill graph-tag-${tag}`} title={`Tag: ${tag}`}>
                          {tag}
                        </span>
                      ) : (
                        <span className="graph-tag-pill graph-tag-untagged" title="No tag set">—</span>
                      )}
                    </td>
                    <td className="num-cell">
                      <span className="num-value">
                        {typeof graph.nodeCount === 'number'
                          ? graph.nodeCount.toLocaleString()
                          : '-'}
                      </span>
                    </td>
                    <td className="status-cell">
                      <span
                        className={`status-pill status-${serCompiled ? 'yes' : 'no'}`}
                        title={serCompiled
                          ? 'SER compiled (GraphStats.SERCompiledAt set)'
                          : 'SER not compiled -- run SER compile to populate'}
                      >
                        {serCompiled ? 'Yes' : 'No'}
                      </span>
                      {serCompiledAt && (
                        <div className="status-meta">{serCompiledAt}</div>
                      )}
                    </td>
                    <td className="status-cell">
                      {metaPending ? (
                        <span className="status-pill status-loading" title="Loading status...">…</span>
                      ) : (
                        <span
                          className={`status-pill status-${meta?.compiled_full ? 'yes' : 'no'}`}
                          title={meta?.compiled_full
                            ? `Compiled artifacts available${meta?.in_redis_cache ? ' on disk and warm in Redis' : ' on disk'}`
                            : 'Not compiled yet -- needs to be compiled before it can be queried'}
                        >
                          {meta?.compiled_full ? 'Yes' : 'No'}
                        </span>
                      )}
                      {meta?.compiled_full && (formatDateTime(meta.compiled_at) || redisCachedAt) && (
                        <div
                          className="status-meta"
                          title={meta?.in_redis_cache
                            ? `Disk compile + Redis hot${meta?.redis_cached_at_approximate ? ' (Redis time approximate)' : ''}`
                            : 'Disk compile only -- not currently in Redis hot cache'}
                        >
                          {formatDateTime(meta.compiled_at) || redisCachedAt}
                          {meta?.in_redis_cache ? ' • hot' : ''}
                        </div>
                      )}
                    </td>
                    <td>{graph.workspaceName}</td>
                    <td className="description">{graph.description || 'N/A'}</td>
                    {showExtraColumns && (
                      <td>
                        <span className={`badge badge-${graph.graphType?.toLowerCase()}`}>
                          {graph.graphType}
                        </span>
                      </td>
                    )}
                    {showExtraColumns && (
                      <td>
                        <span className={`badge badge-${graph.status?.toLowerCase()}`}>
                          {graph.status}
                        </span>
                      </td>
                    )}
                    {showExtraColumns && <td>{graph.isLocked ? 'Yes' : 'No'}</td>}
                    {showExtraColumns && (
                      <td title={graph.createUser} className="created-by">
                        {getTruncatedEmail(graph.createUser)}
                      </td>
                    )}
                    <td>{formatDate(graph.createDate)}</td>
                    <td className="actions">
                      {graph.canEdit && canBuildGraphs && (
                        <button
                          className="gm-action gm-action--build"
                          onClick={() => handleBuildGraph(graph)}
                          title="Build Graph"
                          aria-label="Build Graph"
                        >
                          <MdBuild size={28} aria-hidden="true" />
                        </button>
                      )}
                      {graph.canEdit && (
                        <button
                          className="gm-action gm-action--permissions"
                          onClick={() => handleManagePermissions(graph)}
                          title="Manage Permissions"
                          aria-label="Manage Permissions"
                        >
                          <MdLock size={28} aria-hidden="true" />
                        </button>
                      )}
                      {graph.canEdit && (
                        <button
                          className="gm-action gm-action--edit"
                          onClick={() => handleEditGraph(graph)}
                          title="Edit Graph"
                          aria-label="Edit Graph"
                        >
                          <MdEdit size={28} aria-hidden="true" />
                        </button>
                      )}
                      {graph.canDelete && (
                        <button
                          className="gm-action gm-action--delete"
                          onClick={() => handleDeleteGraph(graph)}
                          title="Delete Graph"
                          aria-label="Delete Graph"
                        >
                          <MdDelete size={28} aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn-page"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages} ({totalCount} total)
              </span>
              <button
                className="btn-page"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Create Graph Modal */}
      {showCreateModal && (
        <div className="graph-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="graph-modal" onClick={(e) => e.stopPropagation()}>
            <div className="graph-modal-header">
              <h2>Create New Graph</h2>
              <button className="graph-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={submitCreateGraph}>
              <div className="graph-modal-body">
                <div className="graph-form-group">
                  <label>Graph Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.graphName}
                    onChange={(e) => setFormData({ ...formData, graphName: e.target.value })}
                    placeholder="Enter graph name"
                  />
                </div>
                <div className="graph-form-group">
                  <label>Workspace *</label>
                  <select
                    required
                    value={formData.workspaceId}
                    onChange={(e) => setFormData({ ...formData, workspaceId: e.target.value })}
                  >
                    <option value="">Select Workspace</option>
                    {workspaces.map((workspace) => (
                      <option key={workspace.workspaceId} value={workspace.workspaceId}>
                        {workspace.workspaceName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="graph-form-group">
                  <label>Description</label>
                  <textarea
                    rows="4"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter graph description"
                  />
                </div>
                <div className="graph-form-row">
                  <div className="graph-form-group">
                    <label>Graph Type *</label>
                    <select
                      value={formData.graphType}
                      onChange={(e) => setFormData({ ...formData, graphType: e.target.value })}
                    >
                      <option value="Hybrid">Hybrid</option>
                      <option value="Causal">Causal</option>
                      <option value="Process">Process</option>
                      <option value="Statistical">Statistical</option>
                      <option value="Router">Router</option>
                      <option value="Ontology">Ontology (EDNL)</option>
                      <option value="DynamicProcess">Dynamic Process</option>
                    </select>
                  </div>
                  <div className="graph-form-group">
                    <label>Status *</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Testing">Testing</option>
                      <option value="Production">Production</option>
                    </select>
                  </div>
                </div>
                <div className="graph-form-group graph-form-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isLocked}
                      onChange={(e) => setFormData({ ...formData, isLocked: e.target.checked })}
                    />
                    <span>Lock Graph (prevent modifications)</span>
                  </label>
                </div>
              </div>
              <div className="graph-modal-footer">
                <button 
                  type="button" 
                  className="graph-btn graph-btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="graph-btn graph-btn-primary" disabled={isCreating}>
                  {isCreating ? 'Creating...' : 'Create Graph'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Graph Modal */}
      {showEditModal && selectedGraph && (
        <div className="graph-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="graph-modal" onClick={(e) => e.stopPropagation()}>
            <div className="graph-modal-header">
              <h2>Edit Graph</h2>
              <button className="graph-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={submitEditGraph}>
              <div className="graph-modal-body">
                <div className="graph-form-group">
                  <label>Graph Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.graphName}
                    onChange={(e) => setFormData({ ...formData, graphName: e.target.value })}
                    placeholder="Enter graph name"
                  />
                </div>
                <div className="graph-form-group">
                  <label>Workspace *</label>
                  <select
                    required
                    value={formData.workspaceId}
                    onChange={(e) => setFormData({ ...formData, workspaceId: e.target.value })}
                    disabled
                  >
                    <option value="">Select Workspace</option>
                    {workspaces.map((workspace) => (
                      <option key={workspace.workspaceId} value={workspace.workspaceId}>
                        {workspace.workspaceName}
                      </option>
                    ))}
                  </select>
                  <small className="graph-form-hint">Workspace cannot be changed after creation</small>
                </div>
                <div className="graph-form-group">
                  <label>Description</label>
                  <textarea
                    rows="4"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter graph description"
                  />
                </div>
                <div className="graph-form-row">
                  <div className="graph-form-group">
                    <label>Graph Type *</label>
                    <select
                      value={formData.graphType}
                      onChange={(e) => setFormData({ ...formData, graphType: e.target.value })}
                    >
                      <option value="Hybrid">Hybrid</option>
                      <option value="Causal">Causal</option>
                      <option value="Process">Process</option>
                      <option value="Statistical">Statistical</option>
                      <option value="Router">Router</option>
                      <option value="Ontology">Ontology (EDNL)</option>
                      <option value="DynamicProcess">Dynamic Process</option>
                    </select>
                  </div>
                  <div className="graph-form-group">
                    <label>Status *</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Testing">Testing</option>
                      <option value="Production">Production</option>
                    </select>
                  </div>
                </div>
                <div className="graph-form-group graph-form-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isLocked}
                      onChange={(e) => setFormData({ ...formData, isLocked: e.target.checked })}
                    />
                    <span>Lock Graph (prevent modifications)</span>
                  </label>
                </div>
                <div className="graph-form-group">
                  <label>Metadata</label>
                  <textarea
                    className="graph-metadata-input"
                    rows="8"
                    value={metadataText}
                    onChange={(e) => {
                      setMetadataText(e.target.value);
                      if (metadataError) setMetadataError(null);
                    }}
                    placeholder={'{\n  "codes": ["4234"],\n  "type": "medicare",\n  "category": "LCD"\n}'}
                    spellCheck={false}
                  />
                  <small className="graph-form-hint">
                    JSON object used to select this graph. Leave empty to clear.
                  </small>
                  {metadataError && <small className="graph-form-error">{metadataError}</small>}
                </div>
              </div>
              <div className="graph-modal-footer">
                <button 
                  type="button" 
                  className="graph-btn graph-btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="graph-btn graph-btn-primary">
                  Update Graph
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Graph Modal - Two Step Confirmation */}
      {showDeleteModal && selectedGraph && (
        <div className="graph-modal-overlay" onClick={handleCancelDelete}>
          <div className="graph-modal graph-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="graph-modal-header graph-modal-header-danger">
              <h2>⚠️ Delete Graph - {deleteStep === 1 ? 'Warning' : 'Final Confirmation'}</h2>
              <button className="graph-modal-close" onClick={handleCancelDelete}>×</button>
            </div>
            <div className="graph-modal-body">
              {deleteStep === 1 ? (
                /* Step 1: First Warning */
                <>
                  <div className="graph-delete-warning-box">
                    <h3 className="graph-delete-warning-title">⛔ DANGER: Irreversible Operation</h3>
                    <p className="graph-delete-warning">
                      You are about to <strong>permanently delete</strong> the graph:
                    </p>
                    <p className="graph-delete-graph-name">"{selectedGraph.graphName}"</p>
                  </div>
                  
                  <div className="graph-delete-data-list">
                    <p><strong>The following data will be PERMANENTLY DELETED:</strong></p>
                    <ul>
                      <li>All nodes and their relationships (connections)</li>
                      <li>All logic gates</li>
                      <li>All uploaded documents</li>
                      <li>All Q&A pairs and contributions</li>
                      <li>All consolidation history</li>
                      <li>All vector embeddings (Pinecone)</li>
                      <li>All processing jobs and relevance scoring data</li>
                    </ul>
                  </div>
                  
                  <div className="graph-delete-final-warning">
                    <p>🚫 <strong>This action CANNOT be undone.</strong></p>
                    <p>There is NO recovery option once deleted.</p>
                  </div>
                </>
              ) : (
                /* Step 2: Final Confirmation with Code */
                <>
                  <div className="graph-delete-warning-box graph-delete-warning-box-final">
                    <h3 className="graph-delete-warning-title">🔴 FINAL CONFIRMATION</h3>
                    <p className="graph-delete-warning">
                      This is your <strong>LAST CHANCE</strong> to cancel.
                    </p>
                    <p className="graph-delete-graph-name">Deleting: "{selectedGraph.graphName}"</p>
                  </div>
                  
                  <p className="graph-delete-confirm-text">
                    Enter 3 digits to confirm permanent deletion:
                  </p>
                  <div className="graph-delete-inputs">
                    {[0, 1, 2].map((index) => (
                      <input
                        key={index}
                        type="text"
                        maxLength="1"
                        className="graph-delete-digit"
                        value={deleteNumbers[index]}
                        disabled={isDeleting}
                        onChange={(e) => {
                          const newNumbers = [...deleteNumbers];
                          newNumbers[index] = e.target.value.replace(/\D/g, '');
                          setDeleteNumbers(newNumbers);
                          if (e.target.value && index < 2) {
                            e.target.nextElementSibling?.focus();
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !deleteNumbers[index] && index > 0) {
                            e.target.previousElementSibling?.focus();
                          }
                        }}
                      />
                    ))}
                  </div>
                  {isDeleting && (
                    <p className="graph-delete-progress">Deleting graph and all related data...</p>
                  )}
                </>
              )}
              {deleteError && <p className="graph-error-text">{deleteError}</p>}
            </div>
            <div className="graph-modal-footer">
              <button 
                type="button" 
                className="graph-btn graph-btn-secondary"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              {deleteStep === 1 ? (
                <button 
                  className="graph-btn graph-btn-warning"
                  onClick={handleFirstConfirmation}
                >
                  I Understand, Continue
                </button>
              ) : (
                <button 
                  className="graph-btn graph-btn-danger"
                  onClick={submitDeleteGraph}
                  disabled={isDeleting || deleteNumbers.join('').length !== 3}
                >
                  {isDeleting ? 'Deleting...' : '🗑️ DELETE PERMANENTLY'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedGraph && (
        <div className="graph-modal-overlay" onClick={() => setShowPermissionsModal(false)}>
          <div className="graph-modal graph-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="graph-modal-header">
              <h2>Manage Permissions: {selectedGraph.graphName}</h2>
              <button className="graph-modal-close" onClick={() => setShowPermissionsModal(false)}>×</button>
            </div>
            <div className="graph-modal-body">
              {/* Grant Permission Section */}
              <div className="graph-permissions-grant">
                <h3>Grant Permission</h3>
                <div className="graph-permissions-grant-form">
                  <div className="graph-form-group">
                    <label>Search User</label>
                    <input
                      type="text"
                      placeholder="Search by name, email, or username..."
                      value={userSearchTerm}
                      onChange={(e) => {
                        setUserSearchTerm(e.target.value);
                        searchUsers(e.target.value);
                      }}
                    />
                    {searchedUsers.length > 0 && (
                      <div className="graph-user-search-results">
                        {searchedUsers.map((user) => (
                          <div
                            key={user.userId}
                            className="graph-user-item"
                            onClick={() => {
                              setSelectedUser(user);
                              setUserSearchTerm(`${user.firstName} ${user.lastName} (${user.email})`);
                              setSearchedUsers([]);
                            }}
                          >
                            <strong>{user.firstName} {user.lastName}</strong>
                            <span>{user.email}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="graph-permissions-controls">
                    <div className="graph-form-group">
                      <label>Permission Type</label>
                      <select
                        value={selectedPermissionType}
                        onChange={(e) => setSelectedPermissionType(e.target.value)}
                      >
                        <option value="Read">Read</option>
                        <option value="Edit">Edit</option>
                        <option value="Delete">Delete</option>
                      </select>
                    </div>
                    <button
                      className="graph-btn graph-btn-primary"
                      onClick={grantPermission}
                      disabled={!selectedUser}
                    >
                      Grant Permission
                    </button>
                  </div>
                </div>
              </div>

              {/* Existing Permissions Section */}
              <div className="graph-permissions-list">
                <h3>Current Permissions</h3>
                {permissionsLoading ? (
                  <p>Loading permissions...</p>
                ) : permissions.length === 0 ? (
                  <p className="graph-no-data">No permissions granted yet</p>
                ) : (
                  <table className="graph-permissions-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Permission</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {permissions.map((perm) => (
                        <tr key={perm.accessControlId}>
                          <td>{perm.firstName} {perm.lastName}</td>
                          <td>{perm.email}</td>
                          <td>
                            <span className={`graph-badge graph-badge-permission-${perm.permissionName.toLowerCase()}`}>
                              {perm.permissionName}
                            </span>
                          </td>
                          <td>
                            <span className={`graph-status ${perm.isActive ? 'graph-status-active' : 'graph-status-inactive'}`}>
                              {perm.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            {perm.isActive && (
                              <button
                                className="graph-action-btn graph-action-btn-revoke"
                                onClick={() => revokePermission(perm.accessControlId)}
                              >
                                Revoke
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div className="graph-modal-footer">
              <button 
                type="button" 
                className="graph-btn graph-btn-secondary"
                onClick={() => setShowPermissionsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <ImportGraphModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={() => loadGraphs()}
        />
      )}
    </div>
  );
}

export default GraphsPage;
